import appstore from "../../appStore";
import { Api } from "../../Services/Api";
import { BookingAuthority, BookingStatus } from "../../Services/BookingEntities";
import { MyStorage } from "../../Storage";
import { Dispatch } from "../Dispatch";
import { ConvertLightweightBookingStatusToBookingTrackingDetails } from "../MyBookings/ConvertAPIContractsToInternalModels";
import { BookingRefreshCycle, GetDueRefreshCycle, NoteRefreshOccurring } from "../MyBookings/Logic/BookingRefreshSchedule";
import { ConsiderRefreshDriverDetailsForVoiceBooking } from "../MyBookings/Logic/RefreshDriverDetails";
import { PhoneToCnpBookingDetails } from "./PhoneToCnpEntities";
import { NoticeType, ToasterType } from "./UI/Notifications/NotificationEntities";

/** Refresh a voice booking's data from the server.
 * This only checks the fields that change over time, like booking status, car number, vehicle location, etc.
 * Be defensive about the amount of work done: Only perform a service call if the booking needs refreshing. 
 * */
export async function ConsiderRefreshingVoiceBooking() {
    // check timer readiness
    var refreshCycle = GetDueRefreshCycle();
    if (refreshCycle == null) return;

    const candidateBooking = appstore.getState().PhoneToCnp.Booking!;

    if (!ShouldRefreshBooking(candidateBooking, refreshCycle)) return;

    NoteRefreshOccurring(refreshCycle);

    await PerformVoiceBookingUpdate(candidateBooking);
}

/** Returns true if the specified booking should be refreshed during this cycle. */
function ShouldRefreshBooking(booking: PhoneToCnpBookingDetails, refreshCycle: BookingRefreshCycle): boolean {

    // finished bookings never update
    if (booking.TrackingInfo.BookingStatusID >= BookingStatus.Completed) return false;

    // long timer: any booking
    if (refreshCycle == BookingRefreshCycle.AllBookings) return true;

    // short timer: active bookings only
    return booking.TrackingInfo.BookingStatusID > BookingStatus.NotDispatched;
}

/** Do actual API call to load the booking details and update Redux store with the response. */
async function PerformVoiceBookingUpdate(updateCandidate: PhoneToCnpBookingDetails) {

    // service call to get new data
    const bookingKey: BookingAuthority = {
        bookingId: updateCandidate.BookingId,
        hashCode: updateCandidate.AccessCode
    }

    const serviceResult = await Api.Booking.GetBookingStatusBulk([bookingKey]);

    // abort on error
    if (!serviceResult.isSuccess) {
        // it's ok to return from here without any action. the refresh timer will keep trying and no need to notify the user.
        return;
    }

    const update = ConvertLightweightBookingStatusToBookingTrackingDetails(serviceResult.value[0]);

    if (update.BookingStatusID !== BookingStatus.Cancelled) {
        ConsiderRefreshDriverDetailsForVoiceBooking(updateCandidate, update);
    }

    if (update.BookingStatusID === BookingStatus.PickedUp) {
        // hide the notice about fixed price not locked in. It is not relevant anymore once the booking is picked up.
        if (appstore.getState().PhoneToCnp.NoticeToDisplay === NoticeType.FixedPriceNotLockedIn) {
            Dispatch.PhoneToCnp.HideNotice();
        }        
    }

    ConsiderDisplayingNoticeAndToasterForNoJob(updateCandidate, update.BookingStatusID);
    ConsiderDisplayingToasterForPickedUpNotCnp(updateCandidate, update.BookingStatusID);
    ConsiderDisplayingDriverRecallNotice(updateCandidate, update.BookingStatusID);
    ConsiderDisplayingTripStartedToaster(updateCandidate, update.BookingStatusID);
    ConsiderDisplayingTripCompletedToaster(updateCandidate, update.BookingStatusID);

    Dispatch.PhoneToCnp.RefreshTrackingInfo(update);    
}

/**
 * When NoJob status is identified for the first time, display a notice and a toaster to inform the user about the status change.
 */
export function ConsiderDisplayingNoticeAndToasterForNoJob(oldBooking: PhoneToCnpBookingDetails, newStatus: BookingStatus) {
    const isPaymentConfirmed = appstore.getState().PhoneToCnp.IsPaymentConfirmed;

    // only for No Job bookings.
    if (newStatus !== BookingStatus.NoJob) return;

    const displayedNotices = MyStorage.BookingNotifications.LoadData() ?? {};
    let displayedNoticesForTheBooking = displayedNotices[oldBooking.BookingId];

    // Display different messages for Fixed Fare booking vs Meter Fare booking.        
    if (oldBooking.Payment !== null || isPaymentConfirmed) {
        // already displayed this notice for this booking, do not display it again.
        if (displayedNoticesForTheBooking && displayedNoticesForTheBooking.includes(NoticeType.FixedFareTripCancelledByDriver)) return;

        Dispatch.PhoneToCnp.ShowNotice(NoticeType.FixedFareTripCancelledByDriver);
    }
    else {
        // already displayed this notice for this booking, do not display it again.
        if (displayedNoticesForTheBooking && displayedNoticesForTheBooking.includes(NoticeType.MeterFareTripCancelledByDriver)) return;

        Dispatch.PhoneToCnp.ShowNotice(NoticeType.MeterFareTripCancelledByDriver);
    }

    // Display toaster for both Fixed and Meter fare bookings.
    Dispatch.PhoneToCnp.ShowToaster(ToasterType.NoJob);
}

/** 
 * If the booking has not been converted to CNP yet, but the status has changed to PickedUp, display a notice to inform the user that the booking has been picked up and cannot be converted to CNP. Display it only once. 
 * */
export function ConsiderDisplayingToasterForPickedUpNotCnp(oldBooking: PhoneToCnpBookingDetails, newStatus: BookingStatus) {
    const isPaymentConfirmed = appstore.getState().PhoneToCnp.IsPaymentConfirmed;

    // early exit if status is not picked up.
    if (newStatus !== BookingStatus.PickedUp) return;

    // already converted to CNP
    if (oldBooking.Payment !== null) return;
    if (isPaymentConfirmed) return;

    const displayedNotices = MyStorage.BookingNotifications.LoadData() ?? {};
    const displayedNoticesForTheBooking = displayedNotices[oldBooking.BookingId];

    // already displayed this notice for this booking, do not display it again.
    if (displayedNoticesForTheBooking && displayedNoticesForTheBooking.includes(NoticeType.FixedPriceNoLongerAvailable)) return;

    Dispatch.PhoneToCnp.ShowNotice(NoticeType.FixedPriceNoLongerAvailable);
}

/** 
 * Display a notice if the booking has been accepted by a driver but then recalled. In this case the status goes back to dispatching states (dispatching, assigned and acknowledge). Display the notice to avoid confusion for the user.
 */
export function ConsiderDisplayingDriverRecallNotice(oldBooking: PhoneToCnpBookingDetails, newStatus: BookingStatus) {

    // current status is accepted or higher, no need to display the notice.
    if (newStatus >= BookingStatus.Accepted) return;

    // old status is lower than accepted, no need to display the notice.
    if (oldBooking.TrackingInfo.BookingStatusID < BookingStatus.Accepted) return;

    Dispatch.PhoneToCnp.ShowNotice(NoticeType.DriverNoLongerAvailable);
}

/**
 * Display a toaster to inform the user that the trip has started (picked up). Display it only once.
 */
function ConsiderDisplayingTripStartedToaster(oldBooking: PhoneToCnpBookingDetails, newStatus: BookingStatus) {

    // only applies for picked up status.
    if (newStatus !== BookingStatus.PickedUp) return;

    // show it only once, when the status changes from accepted to picked up. If the status was already picked up, do not show it again.
    if (oldBooking.TrackingInfo.BookingStatusID === BookingStatus.PickedUp) return;
    Dispatch.PhoneToCnp.ShowToaster(ToasterType.TripStarted);
}

/**
 * Display a toaster to inform the user that the trip has completed. Display it only once.
 */
function ConsiderDisplayingTripCompletedToaster(oldBooking: PhoneToCnpBookingDetails, newStatus: BookingStatus) {

    // only applies for completed status.
    if (newStatus !== BookingStatus.Completed) return;

    // show it only once, when the status changes from picked up to completed. If the status was already completed, do not show it again.
    if (oldBooking.TrackingInfo.BookingStatusID === BookingStatus.Completed) return;
    Dispatch.PhoneToCnp.ShowToaster(ToasterType.TripCompleted);
}